import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { iconPlusCircle } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import { baseStyles, buttonStyles, fileUploadStyles } from '@/styles';
import { ErrorType, UploadExtensionCheck } from '@/types';
import {
  uploadDragEndHandler,
  uploadDragOverHandler,
  uploadDropHandler,
  uploadInputEvent,
} from '@/utils';

@customElement('fg-step-upload')
@localized()
export class FgFileStepUpload extends LitElement {
  @state()
  private isDragging = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  private handleFileData(fileData: {
    file: File | null;
    fileName: string;
    error: ErrorType | null;
  }) {
    const { file, fileName, error } = fileData;

    if (error !== null) {
      this.fileErrorHandler(error);
      return;
    }

    this.fileErrorHandler(null);

    if (!file) {
      return;
    }

    this.startUpload(file, fileName);
  }

  private uploadDropEvent(event: DragEvent, filecheck?: UploadExtensionCheck) {
    const fileData = uploadDropHandler(event, filecheck);

    if (!fileData) {
      return;
    }

    this.isDragging = false;

    this.handleFileData(fileData);
  }

  private uploadFile(event: Event) {
    const fileData = uploadInputEvent(event);

    if (!fileData) {
      return;
    }

    this.handleFileData(fileData);
  }

  private fileErrorHandler(error: ErrorType | null) {
    this.dispatchEvent(new CustomEvent('upload-error', { detail: error }));
  }

  private startUpload(file: File, fileName: string) {
    this.dispatchEvent(
      new CustomEvent('start-upload', {
        detail: { file: file, fileName: fileName },
      }),
    );
  }

  render() {
    const { layout } = this.globalContext;

    return html`
      <div
        class=${this.isDragging ? 'drop-area active' : 'drop-area'}
        @dragover=${(event: DragEvent) => {
          this.isDragging = uploadDragOverHandler(event);
        }}
        @dragleave=${(event: DragEvent) => {
          this.isDragging = uploadDragEndHandler(event);
        }}
        @drop=${(event: DragEvent) => this.uploadDropEvent(event)}
      >
        <strong class="drop-title">
          ${iconPlusCircle} ${t('labels.dropFile')}
        </strong>
        <p class="hr-line"><span>${t('labels.or')}</span></p>
        <label class="button button-primary button-upload" for="file">
          ${t('labels.uploadFile')}
        </label>
        <input
          id="file"
          type="file"
          @change=${(event: Event) => this.uploadFile(event)}
        />
        <span>
          ${t(
            layout === 'retail'
              ? 'labels.optimalFileFormatsRetail'
              : 'labels.optimalFileFormatsWholesale',
          )}
        </span>
      </div>
    `;
  }
  static styles = [
    baseStyles,
    fileUploadStyles,
    buttonStyles,
    css`
      .drop-area {
        min-height: 180px;
      }
      label {
        margin-bottom: 15px;
      }
    `,
  ];
}
